import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

/**
 * A modal form for one create or change. The caller owns the fields and the mutation; the dialog
 * only frames them and shows the API's refusal verbatim, since the API is the validator.
 */
export function FormDialog({
  open,
  title,
  submitLabel,
  canSubmit,
  submitting,
  error,
  onClose,
  onSubmit,
  children,
  restoreFocus = true,
}: {
  open: boolean;
  title: string;
  submitLabel: string;
  canSubmit: boolean;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
  /** False for a dialog opened as the page loads: no control opened it to return focus to. */
  restoreFocus?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      disableRestoreFocus={!restoreFocus}
    >
      <form
        noValidate
        aria-label={title}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit && !submitting) onSubmit();
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {error !== null && (
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            )}
            {children}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={submitting} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit || submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const TONES: Readonly<Record<string, 'success' | 'default' | 'info' | 'warning'>> = {
  active: 'success',
  open: 'success',
  posted: 'success',
  inactive: 'default',
  closed: 'default',
  completed: 'success',
  draft: 'info',
  unreconciled: 'warning',
  reconciled: 'success',
  pending: 'info',
  sent: 'success',
  failed: 'warning',
  // Document statuses (SDD §17.1).
  available: 'success',
  pending_scan: 'info',
  superseded: 'default',
  quarantined: 'warning',
  scan_failed: 'warning',
  // Request statuses (HR leave and employee requests, FRD-HRP-030).
  submitted: 'info',
  approved: 'success',
  rejected: 'warning',
  returned: 'warning',
  cancelled: 'default',
};

/** A status value from the API, shown as-is in a small tinted chip. */
export function StatusChip({ status }: { status: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={TONES[status] ?? 'default'}
      label={status.replace(/_/g, ' ')}
    />
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

/**
 * A drop-down the user can type into to narrow the choices, for lists such as departments or cost
 * centres that grow too long to scan. The value is the option's `value`, `''` when nothing is
 * chosen; an optional field can be cleared back to `emptyLabel`.
 */
export function SearchableSelect({
  label,
  value,
  options,
  onChange,
  required = false,
  disabled = false,
  helperText,
  error,
  emptyLabel = 'None',
}: {
  label: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  helperText?: ReactNode;
  error?: boolean;
  emptyLabel?: string;
}) {
  const labels = new Map(options.map((o) => [o.value, o.label] as const));
  return (
    <Autocomplete<string, false, boolean>
      options={options.map((o) => o.value)}
      value={value === '' ? null : value}
      getOptionLabel={(v) => labels.get(v) ?? v}
      onChange={(_, next) => {
        onChange(next ?? '');
      }}
      disabled={disabled}
      disableClearable={required}
      autoHighlight
      openOnFocus
      noOptionsText="No match"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={required ? undefined : emptyLabel}
          // Merged, not replaced: the autocomplete wires its input through these slots.
          slotProps={{
            ...params.slotProps,
            inputLabel: { ...params.slotProps.inputLabel, shrink: true },
          }}
        />
      )}
    />
  );
}
